import { getShadowElement } from '@tyler-components-web/core';
import { getActiveElement } from 'shadow-utils';
import { RADIO_CONSTANTS } from './radio-constants';
var RadioAdapter = /** @class */ (function () {
    function RadioAdapter(_component) {
        this._component = _component;
        this._shadowRoot = getShadowElement(this._component, RADIO_CONSTANTS.selectors.RADIO);
    }
    RadioAdapter.prototype.connect = function () {
        var labelElement = this._component.querySelector(RADIO_CONSTANTS.selectors.LABEL);
        if (labelElement) {
            labelElement.setAttribute(RADIO_CONSTANTS.attributes.SLOT, RADIO_CONSTANTS.selectors.LABEL);
        }
    };
    RadioAdapter.prototype.setHostAttribute = function (name, value) {
        if (value === void 0) { value = ''; }
        this._component.setAttribute(name, value);
    };
    RadioAdapter.prototype.removeHostAttribute = function (name) {
        this._component.removeAttribute(name);
    };
    RadioAdapter.prototype.addRootClass = function (className) {
        this._shadowRoot.classList.add(className);
    };
    RadioAdapter.prototype.removeRootClass = function (className) {
        this._shadowRoot.classList.remove(className);
    };
    RadioAdapter.prototype.syncDisabledStateWithInput = function () {
        if (this.inputDisabled) {
            this.addRootClass(RADIO_CONSTANTS.classes.DISABLED);
            this.removeRootClass(RADIO_CONSTANTS.classes.ENABLED);
        }
        else {
            this.addRootClass(RADIO_CONSTANTS.classes.ENABLED);
            this.removeRootClass(RADIO_CONSTANTS.classes.DISABLED);
        }
    };
    RadioAdapter.prototype.syncFocusedStateWithInput = function () {
        if (getActiveElement() === this._inputElement) {
            this.addRootClass(RADIO_CONSTANTS.classes.FOCUSED);
        }
        else {
            this.removeRootClass(RADIO_CONSTANTS.classes.FOCUSED);
        }
    };
    RadioAdapter.prototype.syncCheckedStateWithInput = function () {
        if (this.inputChecked) {
            this.addRootClass(RADIO_CONSTANTS.classes.CHECKED);
        }
        else {
            this.removeRootClass(RADIO_CONSTANTS.classes.CHECKED);
        }
    };
    RadioAdapter.prototype.addInputEventListener = function (event, callback) {
        if (!this._inputElement) {
            return;
        }
        this._inputElement.addEventListener(event, callback);
    };
    RadioAdapter.prototype.removeInputEventListener = function (event, callback) {
        if (!this._inputElement) {
            return;
        }
        this._inputElement.removeEventListener(event, callback);
    };
    RadioAdapter.prototype.addInputDisabledAttributeChangeListener = function (callback) {
        this._setupInputDisabledAttributeMutationObserver(callback);
    };
    RadioAdapter.prototype.removeInputDisabledAttributeChangeListener = function () {
        this._cleanupInputDisabledAttributeMutationObserver();
    };
    // Get the collection of all tcw-radios under the containing radiogroup ancestor.
    RadioAdapter.prototype.getRadiosFromContainingRadiogroup = function () {
        var radioGroup = this._getContainingRadioGroup(this._component);
        if (radioGroup == null) {
            return null;
        }
        return radioGroup.querySelectorAll(RADIO_CONSTANTS.elementName);
    };
    RadioAdapter.prototype.syncRadiogroupCheckStyles = function () {
        var radios = this.getRadiosFromContainingRadiogroup();
        if (radios) {
            radios.forEach(function (x) { return x.syncCheckedState(); });
        }
    };
    RadioAdapter.prototype.extendNativeInputSetter = function (callback) {
        var _this = this;
        if (!this._inputElement) {
            return;
        }
        var nativeInput = this._inputElement;
        var inputProto = Object.getPrototypeOf(nativeInput);
        RADIO_CONSTANTS.inputProperties.forEach(function (controlState) {
            var desc = Object.getOwnPropertyDescriptor(inputProto, controlState);
            // We have to check for this descriptor, since some browsers (Safari) don't support its return.
            // See: https://bugs.webkit.org/show_bug.cgi?id=49739
            if (!_this._validPropertyDescriptor(desc)) {
                return;
            }
            if (desc) {
                var nativeInputDesc = {
                    configurable: desc.configurable,
                    enumerable: desc.enumerable,
                    get: desc.get,
                    set: function (state) {
                        desc.set.call(nativeInput, state);
                        callback();
                    }
                };
                Object.defineProperty(nativeInput, controlState, nativeInputDesc);
            }
        });
    };
    RadioAdapter.prototype.revertNativeInputSetter = function () {
        var _this = this;
        if (!this._inputElement) {
            return;
        }
        var nativeInput = this._inputElement;
        var inputProto = Object.getPrototypeOf(nativeInput);
        RADIO_CONSTANTS.inputProperties.forEach(function (controlState) {
            var desc = Object.getOwnPropertyDescriptor(inputProto, controlState);
            // We have to check for this descriptor, since some browsers (Safari) don't support its return.
            // See: https://bugs.webkit.org/show_bug.cgi?id=49739
            if (!_this._validPropertyDescriptor(desc)) {
                return;
            }
            Object.defineProperty(nativeInput, controlState, desc);
        });
    };
    RadioAdapter.prototype._validPropertyDescriptor = function (inputPropDesc) {
        return !!inputPropDesc && typeof inputPropDesc.set === 'function';
    };
    // Recursively crawl up the node tree looking for the containing radiogroup ancestor element.
    RadioAdapter.prototype._getContainingRadioGroup = function (element) {
        if (!element || !element.parentElement) {
            throw new Error('Unable to locate ancestor element with role="radiogroup". When using radios, a parent with this attribute must be applied.');
        }
        var parentElement = element.parentElement;
        var roleValue = parentElement.getAttribute(RADIO_CONSTANTS.attributes.ROLE);
        if (!!roleValue && roleValue.toLocaleLowerCase() === RADIO_CONSTANTS.attributes.RADIOGROUP_ROLE.toLocaleLowerCase()) {
            return parentElement;
        }
        return this._getContainingRadioGroup(parentElement);
    };
    RadioAdapter.prototype._setupInputDisabledAttributeMutationObserver = function (callback) {
        var _this = this;
        if (!this._inputElement) {
            return;
        }
        this._cleanupInputDisabledAttributeMutationObserver();
        var mutationCallback = function (mutationRecords) { return _this._inputDisabledAttributeMutationCallback(mutationRecords, callback); };
        this._inputAttributeMutationObserver = new MutationObserver(mutationCallback);
        var mutationOptions = {
            attributes: true,
            attributeOldValue: true,
            attributeFilter: ['disabled']
        };
        this._inputAttributeMutationObserver.observe(this._inputElement, mutationOptions);
    };
    RadioAdapter.prototype._cleanupInputDisabledAttributeMutationObserver = function () {
        if (this._inputAttributeMutationObserver) {
            this._inputAttributeMutationObserver.disconnect();
            delete this._inputAttributeMutationObserver;
        }
    };
    RadioAdapter.prototype._inputDisabledAttributeMutationCallback = function (mutationRecords, callback) {
        for (var _i = 0, mutationRecords_1 = mutationRecords; _i < mutationRecords_1.length; _i++) {
            var mutationRecord = mutationRecords_1[_i];
            // Skip:
            //   - Non-attribute mutations.
            //   - Mutations without an attribute name.
            //   - Mutations without a matching attribute name.
            //   - Mutations that did not change the attribute value.
            if (mutationRecord.type !== 'attributes'
                || !mutationRecord.attributeName
                || mutationRecord.attributeName !== 'disabled'
                || mutationRecord.target[mutationRecord.attributeName] === mutationRecord.oldValue) {
                continue;
            }
            callback();
        }
    };
    Object.defineProperty(RadioAdapter.prototype, "inputDisabled", {
        get: function () {
            if (!this._inputElement) {
                return false;
            }
            return this._inputElement.disabled;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RadioAdapter.prototype, "inputChecked", {
        get: function () {
            if (!this._inputElement) {
                return false;
            }
            return this._inputElement.checked;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RadioAdapter.prototype, "_inputElement", {
        get: function () {
            if (!this._nativeInputElement) {
                this._nativeInputElement = this._component.querySelector(RADIO_CONSTANTS.selectors.RADIO_INPUT);
            }
            return this._nativeInputElement;
        },
        enumerable: true,
        configurable: true
    });
    return RadioAdapter;
}());
export { RadioAdapter };
